/**
 * GeneralConsent Component
 *
 * General consent form with: Accuracy of Information, Cancellation Policy,
 * Email Communication, Privacy Policy, and Patient Consent sections.
 */

import React from "react";
import {
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Card,
  CardContent,
} from "@material-ui/core";
import SignatureCanvas from "react-signature-canvas";

function GeneralConsent({
  checked,
  onChange,
  answers,
  signatureRef,
  onClearSignature,
  readOnly,
  version,
}) {
  // Note: General consent doesn't have a primary "checked" flag like others
  // It's always rendered, so we check if any of its fields are populated
  const hasContent = true; // Always show general consent

  return (
    <div>
      {readOnly && version && (
        <Card style={{ marginBottom: 16, backgroundColor: "#fffacd" }}>
          <CardContent>
            <Typography variant="body2" style={{ color: "#856404" }}>
              <b>Historical View:</b> This form was submitted on{" "}
              {version.submittedAt?.split("T")[0]} under consent version{" "}
              {version.versionId} and is displayed for reference only.
            </Typography>
          </CardContent>
        </Card>
      )}
      <Typography variant="h4" style={{ textAlign: "left" }}>
        Accuracy of Information
      </Typography>
      <br />
      <Typography variant="body1" style={{ textAlign: "left" }}>
        I certify that the above medical information is correct to my knowledge.
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={answers.Accuracy_IAgree || false}
            onChange={(e) => onChange("Accuracy_IAgree", e.target.checked)}
            color="primary"
            name="Accuracy_IAgree"
            disabled={readOnly}
          />
        }
        label="I agree"
      />
      <br /> <br />
      <Typography variant="h4" style={{ textAlign: "left" }}>
        Cancellation Policy
      </Typography>
      <br />
      <Typography variant="body1" style={{ textAlign: "left" }}>
        Our policy is as follows:
        <ul>
          <li>
            We kindly ask that you provide at least 24 hours&apos; notice if you
            need to reschedule your appointment. This allows us to offer the
            time slot to another patient who may be waiting.
          </li>
          <li>
            Missed appointments without 24 hours&apos; notice will be subject to
            the full appointment fee.{" "}
          </li>
          <li>
            If you arrive late for your appointment, we will do our best to
            accommodate you. However, your visit may need to be shortened to
            avoid delaying subsequent appointments.{" "}
          </li>
        </ul>
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={answers.CancellationPolicy_IAgree || false}
            onChange={(e) =>
              onChange("CancellationPolicy_IAgree", e.target.checked)
            }
            color="primary"
            name="CancellationPolicy_IAgree"
            disabled={readOnly}
          />
        }
        label="I have read and understand the Appointment Cancellation Policy and agree to be bound by its terms"
      />
      <br /> <br />
      <Typography variant="h4" style={{ textAlign: "left" }}>
        Email Communication
      </Typography>
      <br />
      <Typography variant="body1" style={{ textAlign: "left" }}>
        To facilitate communication, our clinic offers email as an option.
        Please note, email may not be completely secure, and there are risks
        associated with sharing sensitive health information via email.
        <br /> <br />
        Email is suitable for non-urgent matters (e.g., appointments, general
        questions). Please avoid sending sensitive or urgent health information
        via email.
        <br /> <br />
        By agreeing, you acknowledge the risks of email communication and
        consent to using email for communication with our clinic. You can
        withdraw consent at any time.
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={answers.EmailCommunication_IAgree || false}
            onChange={(e) =>
              onChange("EmailCommunication_IAgree", e.target.checked)
            }
            color="primary"
            name="EmailCommunication_IAgree"
            disabled={readOnly}
          />
        }
        label="I Agree"
      />
      <br /> <br />
      <Typography variant="h4" style={{ textAlign: "left" }}>
        Consent for the Collection, Use, and Disclosure of Personal Information
        (PIPEDA/PHIPA)
      </Typography>
      <br />
      <Typography variant="body1" style={{ textAlign: "left" }}>
        We understand the importance of protecting the privacy of your personal
        information, and we are committed to handling your data responsibly in
        terms of collection, use, and disclosure. Privacy of your medical
        records and information is kept in accordance with the Personal
        Information Protection and Electronic Documents Act (PIPEDA) and the
        Personal Health Information Protection Act (PHIPA), legislated by the
        Canadian government in 2004.
        <br /> <br />
        As such, we strive to ensure that:
        <ul>
          <li>Only necessary information is collected about you. </li>
          <li>Your information is shared only with your consent. </li>
          <li>
            The storage, retention, and destruction of your personal information
            adhere to current legislation and privacy protection protocols.{" "}
          </li>
          <li>
            Our privacy practices align with privacy laws, the standards set by
            our regulatory body, and legal requirements.{" "}
          </li>
        </ul>
        We will collect, use, and disclose information about you for the
        following purposes:
        <ul>
          <li>
            To assess your health needs and advise you of treatment
            options.{" "}
          </li>
          <li>
            To communicate with you and remind you of upcoming
            appointments.{" "}
          </li>
          <li>
            To allow us to efficiently follow-up for treatment, care, and
            billing.{" "}
          </li>
          <li>To communicate with other members of your health care team. </li>
          <li>
            To ensure compliance with regulatory requirements and legal
            obligations, including requirements to advise authorities of child
            abuse and to report diseases and individuals who may be an imminent
            threat to themselves or others.{" "}
          </li>
          <li>
            To invoice for goods and services, process payments, and collect
            unpaid accounts.{" "}
          </li>
          <li>To process and submit insurance claims on your behalf </li>
        </ul>
        I understand that authorized practitioners and administrative staff at
        Appleby Wellness Centre involved in my care may access my personal
        health information as needed, and that all such access will be handled
        in accordance with strict confidentiality and privacy standards.
        <br /> <br />
        By agreeing, you acknowledge the risks of email communication and
        consent to using email for communication with our clinic. You can
        withdraw consent at any time.
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={answers.PrivacyPolicy_IAgree || false}
            onChange={(e) => onChange("PrivacyPolicy_IAgree", e.target.checked)}
            color="primary"
            name="PrivacyPolicy_IAgree"
            disabled={readOnly}
          />
        }
        label="I Agree"
      />
      <br /> <br />
      <Typography variant="h4" style={{ textAlign: "left" }}>
        Patient Consent
      </Typography>
      <br />
      <Typography variant="body1" style={{ textAlign: "left" }}>
        I have reviewed the above information that explains how my practitioner
        and administrative staff at Appleby Wellness Centre will use my personal
        information. I agree that my authorized practitioners and the team at
        Appleby Wellness Centre can collect, use, and disclose my personal
        information for the purposes listed above.
      </Typography>
      <Typography variant="body1" style={{ textAlign: "left" }}>
        Please sign in the box below.
      </Typography>
      <div
        style={{
          borderStyle: "dotted",
          borderWidth: 2,
          borderColor: "#D3D3D3",
          width: 530,
          height: 240,
          borderRadius: 4,
        }}
      >
        <SignatureCanvas
          penColor="black"
          ref={signatureRef}
          canvasProps={{
            width: 530,
            height: 240,
            className: "consentGeneralSig",
          }}
          disabled={readOnly}
        />
      </div>
      <br />
      {!readOnly && <Button onClick={onClearSignature}>Clear</Button>}
      <br />
    </div>
  );
}

export default GeneralConsent;
